import React, { useEffect, useState, createContext, useContext, useMemo } from 'react';
// contexts/AuthContext.tsx
import { useNavigate } from 'react-router-dom';
import { consumePostLoginRedirect } from '../utils/postLoginRedirect';
import { useToast } from './ToastContext';
import { supabase } from '../supabase/config';
import type { User, Session } from '@supabase/supabase-js';
import { type UserRole } from '../types/roles';
import { userService } from '../services/userService';
import { impersonationService } from '../services/impersonationService';

// Define a custom user type that includes our app-specific fields
export interface CustomUser {
  id: string;
  email?: string;
  user_metadata?: any;
  app_metadata?: any;
  role?: UserRole;
  name?: string;
  photoUrl?: string;
  curriculumArea?: string;
  department?: string;
  staffCode?: string;
  // staff.staff_id — the staff table primary key. Distinct from staffCode
  // (staff.staff_code, the grid/allocation key); the two differ for ~70% of
  // staff. Used as the teaching-intentions identifier fallback when a staff
  // member has no staff_code.
  staffId?: string;
  fte?: number;
  status?: 'active' | 'inactive' | 'pending' | 'suspended';
  permissions?: string[];
  isImpersonating?: boolean;
  originalUser?: CustomUser;
  /**
   * True when this profile came from buildDefaultUser rather than the staff
   * directory — i.e. we could not read the real row and fell back to the
   * baseline 'teacher' role.
   *
   * Provisional profiles must never be persisted to localStorage or preserved
   * across auth events. Without this flag a single transient lookup failure
   * writes role:'teacher' to storage, and the email-match preserve branch in
   * onAuthStateChange then keeps re-using it and skips the re-fetch — so the
   * user stays downgraded until they manually clear site data.
   */
  isProvisional?: boolean;
}

// Result of a staff-directory lookup. The outcomes are handled differently by
// the sign-in flow: 'not_found' and 'inactive' block the user; 'error' keeps the
// session and falls back to a default profile.
type LookupResult =
  | { outcome: 'found'; data: Partial<CustomUser> }
  | { outcome: 'not_found' }
  // Staff row exists but is not active. Must block, because pit.current_tenant()
  // resolves the tenant by matching an ACTIVE staff row on email — an inactive
  // member gets NULL, and every RLS policy in pit is fail-closed against it. They
  // would sign in successfully and then find every page silently empty, with
  // nothing saying why. See issue #139.
  | { outcome: 'inactive'; email: string }
  | { outcome: 'error' };

interface AuthContextType {
  user: CustomUser | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithMicrosoft: () => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  impersonateUser: (targetUserEmail: string) => Promise<void>;
  stopImpersonation: () => void;
  isImpersonating: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Bumped to _v2 deliberately. Profiles cached by the previous build carry no
// isProvisional flag, so a stale fallback ('teacher') written before that flag
// existed would still be trusted and preserved. Changing the key retires every
// such cache on next load instead of asking people to clear site data.
const USER_STORAGE_KEY = 'pit_user_data_v2';
const LEGACY_USER_STORAGE_KEYS = ['pit_user_data'];

// Build a safe default profile for an authenticated session when the staff
// directory can't be read (transient backend error). Grants only the baseline
// 'teacher' role so a lookup failure can never escalate privileges.
const buildDefaultUser = (sessionUser: { id: string; email: string; user_metadata?: any; app_metadata?: any }): CustomUser => {
  const localPart = sessionUser.email.split('@')[0];
  return {
    id: sessionUser.id,
    email: sessionUser.email,
    user_metadata: sessionUser.user_metadata,
    app_metadata: sessionUser.app_metadata,
    name: sessionUser.user_metadata?.full_name || localPart || 'User',
    role: 'teacher',
    curriculumArea: 'General',
    department: 'General',
    staffCode: localPart.toUpperCase(),
    staffId: localPart.toUpperCase(),
    fte: 1.0,
    status: 'active',
    permissions: ['teacher'],
    // Marks this as a fallback, not a resolved profile. See CustomUser.
    isProvisional: true,
  };
};

export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  // CRITICAL: Initialize user state from localStorage to preserve across refreshes
  const [user, setUser] = useState<CustomUser | null>(() => {
    try {
      // Retire any pre-_v2 cache before reading, so a stale fallback profile
      // written by the previous build can never be re-hydrated.
      LEGACY_USER_STORAGE_KEYS.forEach((k) => {
        try { localStorage.removeItem(k); } catch { /* ignore */ }
      });
      const stored = localStorage.getItem(USER_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        console.log('AuthContext: Restored user from localStorage:', {
          name: parsed.name,
          role: parsed.role,
          email: parsed.email
        });
        return parsed;
      }
    } catch (error) {
      console.error('AuthContext: Failed to restore user from localStorage:', error);
    }
    return null;
  });

  // CRITICAL: Use a ref to track current user for auth listener
  // This prevents stale closure issues where the listener has old user data
  const userRef = React.useRef<CustomUser | null>(user);
  React.useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Use a ref to track OAuth callback processing to avoid triggering useEffect re-runs
  const isProcessingOAuthCallbackRef = React.useRef(false);

  const [loading, setLoading] = useState(true);
  // Track the latest loading value in a ref so the mount-time safety timer reads
  // the current state, not the (stale) value captured when the effect ran.
  const loadingRef = React.useRef(loading);
  React.useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);
  const [error, setError] = useState<string | null>(null);
  const [originalAdmin, setOriginalAdmin] = useState<CustomUser | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const {
    addToast
  } = useToast();
  const navigate = useNavigate();

  // Debug log when context is initialized
  console.log('AuthContext: Initializing context');

  // CRITICAL: Save user data to localStorage whenever it changes
  // This preserves role and other data across page refreshes and prevents role switching
  useEffect(() => {
    if (user?.isProvisional) {
      // Never cache a fallback profile. Persisting it is what made a one-off
      // lookup failure downgrade someone to 'teacher' permanently: the value
      // was re-hydrated on every load and then preserved on email match.
      // Drop any previously cached profile too, so a stale one cannot survive.
      try {
        localStorage.removeItem(USER_STORAGE_KEY);
      } catch { /* ignore */ }
      console.warn('AuthContext: provisional profile in use - not caching, will re-resolve');
    } else if (user) {
      try {
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
        console.log('AuthContext: Saved user to localStorage:', {
          name: user.name,
          role: user.role,
          email: user.email,
          isImpersonating: user.isImpersonating
        });
      } catch (error) {
        console.error('AuthContext: Failed to save user to localStorage:', error);
      }
    } else {
      // Clear localStorage when user signs out
      localStorage.removeItem(USER_STORAGE_KEY);
      console.log('AuthContext: Cleared user from localStorage');
    }
  }, [user]);

  // Restore impersonation state when user is loaded from localStorage
  useEffect(() => {
    if (user?.isImpersonating && user.originalUser) {
      console.log('AuthContext: Restoring impersonation state from localStorage');
      setIsImpersonating(true);
      setOriginalAdmin(user.originalUser);
    } else {
      setIsImpersonating(false);
      setOriginalAdmin(null);
    }
  }, [user?.isImpersonating, user?.originalUser]);

  // Helper function to lookup complete user data by email from User Management.
  //
  // Returns a discriminated result so callers can tell the three cases apart:
  //   'found'     - staff row located; `data` holds the profile
  //   'not_found' - backend reachable, but no staff row for this email
  //   'error'     - lookup could not complete (network/timeout/RLS/rate limit)
  //
  // This distinction matters: the OAuth callback must only sign a user out on a
  // genuine 'not_found'. Collapsing 'error' into 'not_found' (the old behaviour,
  // where both returned null) meant any transient Supabase hiccup during the
  // callback signed the user straight back out — surfacing as an "authentication
  // timeout" / failed login.
  const lookupUserByEmail = async (email: string): Promise<LookupResult> => {
    try {
      // Normalize email to lowercase for case-insensitive matching
      const normalizedEmail = email.toLowerCase().trim();
      console.log('AuthContext: Looking up user data for email:', email);
      console.log('AuthContext: Normalized email for lookup:', normalizedEmail);
      console.log('AuthContext: Starting database query...');

      // Add a timeout for the database query so a slow/rate-limited backend can
      // never hang the sign-in flow.
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Database query timeout')), 8000); // Reduced from 10s to 8s
      });

      // Self-heal the auth-account link before reading the staff row.
      // Staff imported with a staff.id that doesn't equal their auth.uid() and a
      // NULL auth_user_id are otherwise locked out of their own teaching
      // intention by RLS, and cannot link themselves (staff RLS forbids it). This
      // SECURITY DEFINER RPC binds the caller's own staff row (matched on the JWT
      // email) to their own auth.uid(). Idempotent and best-effort: never block
      // sign-in if it fails.
      try {
        // Best-effort and time-boxed: never let this RPC stall sign-in.
        const linkTimeout = new Promise<{ error: { message: string } | null }>((resolve) =>
          setTimeout(() => resolve({ error: { message: 'link_my_staff_account timeout' } }), 4000)
        );
        const { error: linkError } = await Promise.race([
          supabase.rpc('link_my_staff_account'),
          linkTimeout,
        ]);
        if (linkError) {
          console.warn('AuthContext: link_my_staff_account failed (non-fatal):', linkError.message);
        }
      } catch (linkError) {
        console.warn('AuthContext: link_my_staff_account threw (non-fatal):', linkError);
      }

      // Use ilike for case-insensitive email matching
      const queryPromise = supabase
        .from('staff')
        .select('*')
        .ilike('email', normalizedEmail)
        .single();

      const { data: staffData, error } = await Promise.race([queryPromise, timeoutPromise]) as any;

      // The query above deliberately does NOT filter on status, so an inactive
      // member is distinguishable from an unregistered one and can be told which
      // they are. Both block sign-in.
      if (staffData && staffData.status && staffData.status !== 'active') {
        console.warn('AuthContext: Staff row found but not active:', staffData.status);
        return { outcome: 'inactive', email: normalizedEmail };
      }

      console.log('AuthContext: Database query completed:', {
        hasData: !!staffData,
        errorCode: error?.code,
        errorMessage: error?.message,
        timestamp: new Date().toISOString()
      });

      if (error) {
        if (error.code === 'PGRST116') { // "not found" - backend reached, no row
          console.log('AuthContext: User not found in database (PGRST116)');
          return { outcome: 'not_found' };
        } else {
          // Any other error means the lookup did not complete. Report it as
          // 'error' (not 'not_found') so the callback keeps the session instead
          // of signing the user out on a transient failure.
          console.error('AuthContext: Database error:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          return { outcome: 'error' };
        }
      }

      if (staffData) {
        console.log('AuthContext: Found user data in User Management:', {
          name: staffData.name,
          role: staffData.role,
          department: staffData.department,
          curriculumArea: staffData.curriculum_area,
          status: staffData.status,
          timestamp: new Date().toISOString()
        });

        return {
          outcome: 'found',
          data: {
            role: (staffData.role || staffData.preferences?.role) as UserRole || 'teacher',
            name: staffData.name,
            curriculumArea: staffData.curriculum_area,
            department: staffData.department,
            staffCode: staffData.staff_code,
            staffId: staffData.staff_id,
            fte: staffData.fte || 1.0,
            status: staffData.status || 'active',
            permissions: staffData.permissions || []
          }
        };
      } else {
        console.log('AuthContext: No user found in User Management for email:', email);
        return { outcome: 'not_found' };
      }
    } catch (error: any) {
      console.error('AuthContext: Unexpected error in lookupUserByEmail:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
        timestamp: new Date().toISOString()
      });

      // Enhanced error recovery - don't block authentication
      if (error.message?.includes('timeout') || error.message?.includes('Database query timeout')) {
        console.log('AuthContext: Database timeout - continuing with offline mode');
        addToast('Database timeout - working in offline mode', 'warning');
      } else if (error.message?.includes('fetch') || error.message?.includes('network')) {
        console.log('AuthContext: Network error - continuing with offline mode');
        addToast('Network error - working in offline mode', 'warning');
      } else {
        console.log('AuthContext: Unknown error - continuing with default profile');
        addToast('Database unavailable - using default profile', 'warning');
      }

      // Lookup did not complete - report 'error' so callers keep the session
      // and fall back to a default profile rather than signing the user out.
      return { outcome: 'error' };
    }
  };


  // Handle OAuth callback
  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log('AuthContext: Handling OAuth callback...');
        isProcessingOAuthCallbackRef.current = true;
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('AuthContext: OAuth callback error:', error);
          setError('Login failed. Please try again.');
          addToast('Login failed. Please try again.', 'error');
          return;
        }
        
        if (data.session?.user?.email) {
          const sessionUser = {
            id: data.session.user.id,
            email: data.session.user.email,
            user_metadata: data.session.user.user_metadata,
            app_metadata: data.session.user.app_metadata,
          };
          console.log('AuthContext: OAuth session found:', sessionUser.email);

          // Look up user data from the staff directory (source of truth).
          const lookup = await lookupUserByEmail(sessionUser.email);

          if (lookup.outcome === 'found') {
            const userData = lookup.data;
            // Check if user is active
            if (userData.status === 'inactive' || userData.status === 'suspended') {
              console.log(`AuthContext: User ${sessionUser.email} has status ${userData.status}, blocking login`);
              addToast(`Your account is ${userData.status}. Please contact your administrator to activate your account.`, 'error');
              await supabase.auth.signOut();
              navigate('/login');
              return;
            }

            // Allow pending status - they'll be prompted to complete setup
            if (userData.status === 'pending') {
              console.log(`AuthContext: User ${sessionUser.email} has pending status, allowing login with notice`);
              addToast('Your account is pending. Please complete your profile setup.', 'warning');
            }

            // Create custom user object
            const customUser: CustomUser = {
              ...sessionUser,
              ...userData,
              name: userData.name || sessionUser.user_metadata?.full_name || sessionUser.email.split('@')[0] || 'User'
            };

            console.log('AuthContext: Setting user from OAuth with User Management data:', customUser.name);
            setUser(customUser);
            if (userData.status === 'active') {
              addToast('Login successful', 'success');
            }
            navigate(consumePostLoginRedirect() ?? '/dashboard', { replace: true });
          } else if (lookup.outcome === 'inactive') {
            // Signing them in would give a blank app: pit RLS is fail-closed and
            // pit.current_tenant() only resolves an ACTIVE staff row (#139).
            console.error('AuthContext: Staff account is inactive:', lookup.email);
            addToast(`Your account (${lookup.email}) is marked inactive, so PIT cannot show you any data. Please contact your administrator.`, 'error');
            await supabase.auth.signOut();
            navigate('/login');
          } else if (lookup.outcome === 'not_found') {
            // Backend reachable, but this email has no staff row - genuinely
            // unregistered. This is the only case that should block sign-in.
            console.error('AuthContext: User not found in staff database:', sessionUser.email);
            addToast(`Your account (${sessionUser.email}) is not registered in the system. Please contact your administrator to set up your account.`, 'error');
            await supabase.auth.signOut();
            navigate('/login');
          } else {
            // lookup.outcome === 'error': the directory could not be read
            // (network/timeout/rate limit). Do NOT sign the user out - keep the
            // valid session and continue with a baseline profile so a transient
            // backend hiccup never presents as a failed/"timed-out" login.
            console.warn('AuthContext: Staff lookup failed during callback - continuing with default profile');
            const defaultUser = buildDefaultUser(sessionUser);
            setUser(defaultUser);
            addToast(`Welcome ${defaultUser.name}! Some profile details couldn't be loaded - please refresh in a moment.`, 'warning');
            navigate(consumePostLoginRedirect() ?? '/dashboard', { replace: true });
          }
        }
      } catch (error) {
        console.error('AuthContext: OAuth callback error:', error);
        setError('Login failed. Please try again.');
        addToast('Login failed. Please try again.', 'error');
      } finally {
        isProcessingOAuthCallbackRef.current = false;
      }
    };
    
    // Check if we're on a callback URL
    const url = new URL(window.location.href);
    if (url.searchParams.get('code')) {
      handleAuthCallback();
    }
  }, [addToast, navigate]);

  // Listen for auth state changes with enhanced handling
  useEffect(() => {
    console.log('AuthContext: Setting up auth state change listener...');

    // CRITICAL: Check for existing session on mount
    // This ensures we don't lose user data on page refresh
    const checkInitialSession = async () => {
      console.log('AuthContext: Checking for existing session on mount...');
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user?.email) {
        console.log('AuthContext: Found existing session for:', session.user.email);

        // Use ref to get current user value (prevents stale closure)
        const currentUser = userRef.current;
        // Only preserve a REAL resolved profile. Preserving a provisional one
        // skips the re-fetch below, which is precisely how a transient lookup
        // failure became a permanent 'teacher' downgrade.
        if (currentUser && !currentUser.isProvisional && currentUser.email === session.user.email) {
          console.log('AuthContext: User already loaded from localStorage, preserving:', {
            name: currentUser.name,
            role: currentUser.role,
            email: currentUser.email,
            restoredFromLocalStorage: true
          });
          setLoading(false);
          return; // Don't re-fetch, keep existing data from localStorage
        }

        // Otherwise trigger the auth state change handler
        console.log('AuthContext: No existing user data, will trigger SIGNED_IN event');
      } else {
        console.log('AuthContext: No existing session found');
        setLoading(false);
      }
    };

    checkInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('AuthContext: Auth state changed:', {
        event,
        email: session?.user?.email || 'No user',
        hasSession: !!session,
        timestamp: new Date().toISOString()
      });

      // Skip processing if OAuth callback is already being handled
      if (isProcessingOAuthCallbackRef.current) {
        console.log('AuthContext: Skipping auth state change - OAuth callback already processing');
        return;
      }

      // Handle sign out events immediately - ONLY for explicit sign outs
      if (event === 'SIGNED_OUT') {
        console.log('AuthContext: User signed out explicitly');
        setUser(null);
        setLoading(false);
        setError(null);
        return;
      }

      // If there's no session, clear user (but this should rarely happen)
      if (!session?.user) {
        console.log('AuthContext: No session found, clearing user');
        setUser(null);
        setLoading(false);
        return;
      }

      // For ANY auth event with a valid session, process the user
      // This includes: SIGNED_IN, TOKEN_REFRESHED, INITIAL_SESSION, USER_UPDATED, etc.
      if (session?.user?.email) {
        console.log('AuthContext: Processing authenticated user:', {
          userId: session.user.id,
          email: session.user.email,
          provider: session.user.app_metadata?.provider,
          lastSignIn: session.user.last_sign_in_at,
          event: event,
          hasExistingUser: !!user
        });

        // CRITICAL FIX: For ANY auth event, if we already have valid user data for this email,
        // preserve it instead of re-fetching from database. This prevents database timeouts
        // from overwriting admin roles with default teacher roles.
        // Use userRef.current to get the LATEST user value (prevents stale closure issue)
        const currentUser = userRef.current;
        // Only preserve a REAL resolved profile. Preserving a provisional one
        // skips the re-fetch below, which is precisely how a transient lookup
        // failure became a permanent 'teacher' downgrade.
        if (currentUser && !currentUser.isProvisional && currentUser.email === session.user.email) {
          console.log('AuthContext: User already loaded with valid data, preserving:', {
            name: currentUser.name,
            role: currentUser.role,
            email: currentUser.email,
            event: event,
            preservedFromRef: true
          });
          // Just update the session IDs but keep all the user data intact
          setUser({
            ...currentUser,
            id: session.user.id,
            user_metadata: session.user.user_metadata,
            app_metadata: session.user.app_metadata
          });
          setLoading(false);
          return; // Don't re-fetch from database - we already have valid data
        }

        const sessionUser = {
          id: session.user.id,
          email: session.user.email,
          user_metadata: session.user.user_metadata,
          app_metadata: session.user.app_metadata,
        };
        const userEmail = sessionUser.email;

        // For all users, attempt database lookup with proper timeout protection
        console.log('AuthContext: Starting database lookup for:', userEmail);
        const lookupStartTime = Date.now();

        let lookup: LookupResult = { outcome: 'error' };
        try {
          // 12s outer bound so a slow query can't wedge the auth listener.
          const lookupPromise = lookupUserByEmail(userEmail);
          const timeoutPromise = new Promise<LookupResult>((resolve) => {
            setTimeout(() => {
              console.log('AuthContext: Database lookup timeout after 12s, using default profile');
              resolve({ outcome: 'error' });
            }, 12000);
          });

          lookup = await Promise.race([lookupPromise, timeoutPromise]);
          const lookupDuration = Date.now() - lookupStartTime;
          console.log('AuthContext: Database lookup completed:', {
            duration: `${lookupDuration}ms`,
            outcome: lookup.outcome,
            isTimeout: lookupDuration >= 12000,
          });
        } catch (error) {
          console.error('AuthContext: Database lookup failed, using default profile:', error);
          lookup = { outcome: 'error' };
        }

        if (lookup.outcome === 'found') {
          const userData = lookup.data;
          // Check if user is active
          if (userData.status === 'inactive' || userData.status === 'suspended') {
            console.log(`AuthContext: User ${userEmail} has status ${userData.status}, blocking login`);
            addToast(`Your account is ${userData.status}. Please contact your administrator to activate your account.`, 'error');
            await supabase.auth.signOut();
            setUser(null);
            setLoading(false);
            return;
          }

          // Allow pending status - they'll be prompted to complete setup
          if (userData.status === 'pending') {
            console.log(`AuthContext: User ${userEmail} has pending status, allowing login with notice`);
            addToast('Your account is pending. Please complete your profile setup.', 'warning');
          }

          // Create custom user object
          const customUser: CustomUser = {
            ...sessionUser,
            ...userData,
            name: userData.name || sessionUser.user_metadata?.full_name || userEmail.split('@')[0] || 'User'
          };

          console.log('AuthContext: Setting user from Supabase auth with User Management data:', {
            name: customUser.name,
            role: customUser.role,
            department: customUser.department,
            curriculumArea: customUser.curriculumArea,
            status: customUser.status
          });
          setUser(customUser);
        } else {
          // 'not_found' or 'error' - fall back to a baseline profile so an
          // existing session is never dropped on a token-refresh / lookup
          // hiccup. (The primary login path in the OAuth callback is where a
          // genuine 'not_found' blocks an unregistered user.)
          console.log('AuthContext: No staff profile resolved, using default profile. Outcome:', lookup.outcome);
          const defaultUser = buildDefaultUser(sessionUser);
          setUser(defaultUser);
          if (lookup.outcome === 'inactive') {
            addToast(`Your account is marked inactive, so PIT cannot show you any data. Please contact your administrator.`, 'warning');
          } else if (lookup.outcome === 'not_found') {
            addToast(`Welcome ${defaultUser.name}! Please contact your administrator to set up your profile.`, 'info');
          }
          navigate('/dashboard');
        }
        setLoading(false);
      } else {
        // This should rarely happen - only when there's a session but no email
        console.log('AuthContext: Auth event with session but no email, ignoring:', {
          event: event,
          hasSession: !!session,
          hasUser: !!session?.user
        });
        setLoading(false);
      }
    });
    
    // Safety net: clear a stuck loading state after 15s. Reads refs (not the
    // stale state captured at mount) so it only reports a timeout when we are
    // genuinely still loading with no user resolved - never after a sign-in
    // that has already succeeded.
    const timer = setTimeout(() => {
      if (loadingRef.current) {
        console.log('AuthContext: Timeout reached (15s) - clearing loading state only');
        setLoading(false);

        // If there's no user at this point, it's a legitimate timeout
        if (!userRef.current) {
          console.log('AuthContext: No user set after timeout - authentication may have failed');
          setError('Authentication took too long. Please try signing in again.');
          addToast('Authentication timeout. Please try again.', 'warning');
        }
      }
    }, 15000); // 15 second timeout to allow for slower connections and database lookups (up to 12s)
    
    return () => {
      console.log('AuthContext: Cleaning up auth state listener');
      subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  // Login with Microsoft with enhanced error handling
  const loginWithMicrosoft = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('AuthContext: Attempting Microsoft login...');
      console.log('AuthContext: Current origin:', window.location.origin);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          scopes: 'email profile openid',
          redirectTo: `${window.location.origin}/dashboard`
        }
      });
      
      console.log('AuthContext: Supabase OAuth response:', { data, error });
      
      if (error) {
        console.error('AuthContext: Supabase OAuth error:', error);
        throw error;
      }
      
      if (!data?.url) {
        console.error('AuthContext: No OAuth URL returned from Supabase');
        throw new Error('No OAuth URL returned from Supabase');
      }
      
      console.log('AuthContext: Microsoft OAuth initiated, redirecting to:', data.url);
      // The actual login handling will be done in the auth state change listener
      
    } catch (error: any) {
      console.error('AuthContext: Microsoft login error:', error);
      // Enhanced error reporting
      let errorMessage = 'Microsoft login failed.';
      if (error.message) {
        errorMessage = `Login failed: ${error.message}`;
      }
      setError(errorMessage);
      addToast(errorMessage, 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  
  // Login function (email/password)
  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      console.log('AuthContext: Attempting email/password login...');
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        throw error;
      }
      
      console.log('AuthContext: Email/password login successful:', data.user?.email);
      
      // The actual user setup will be handled by the auth state change listener
      addToast('Login successful', 'success');
      navigate('/dashboard');
      
    } catch (error: any) {
      console.error('AuthContext: Login error:', error);
      // Enhanced error reporting
      let errorMessage = 'Login failed. Please check your credentials.';
      if (error.message) {
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password.';
        } else if (error.message.includes('Too many requests')) {
          errorMessage = 'Too many unsuccessful login attempts. Please try again later.';
        } else {
          errorMessage = `Login failed: ${error.message}`;
        }
      }
      setError(errorMessage);
      addToast(errorMessage, 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout function with enhanced error handling and comprehensive cleanup
  const logout = async () => {
    try {
      setLoading(true);
      console.log('AuthContext: Logging out...');

      // Clear all authentication-related storage before signout
      console.log('AuthContext: Clearing authentication storage...');

      // Clear user data immediately
      localStorage.removeItem(USER_STORAGE_KEY);

      // Clear impersonation state
      localStorage.removeItem('impersonation');

      // Clear any cached auth tokens
      const supabaseKey = `sb-${supabase.supabaseUrl.split('//')[1].split('.')[0]}-auth-token`;
      localStorage.removeItem(supabaseKey);

      // Clear any other auth-related storage
      Object.keys(localStorage).forEach(key => {
        if (key.includes('supabase') || key.includes('auth') || key.includes('token')) {
          console.log(`AuthContext: Clearing storage key: ${key}`);
          localStorage.removeItem(key);
        }
      });

      // Clear session storage as well
      sessionStorage.clear();

      console.log('AuthContext: Storage cleared, signing out from Supabase...');

      // Sign out from Supabase
      await supabase.auth.signOut();
      console.log('AuthContext: Signed out from Supabase');

      // Clear user state
      setUser(null);
      setOriginalAdmin(null);
      setIsImpersonating(false);
      setError(null);

      addToast('Logged out successfully', 'success');
      navigate('/login');
    } catch (error) {
      console.error('AuthContext: Logout error:', error);

      // Even if logout fails, clear local state to prevent hanging
      console.log('AuthContext: Logout error, but clearing local state anyway');
      setUser(null);
      setOriginalAdmin(null);
      setIsImpersonating(false);
      setError(null);

      // Force clear all storage on error
      localStorage.clear();
      sessionStorage.clear();

      addToast('Logged out (forced cleanup)', 'warning');
      navigate('/login');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      console.log('AuthContext: Creating new account...');
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password
      });
      
      if (error) {
        throw error;
      }
      
      console.log('AuthContext: Account created successfully:', data.user?.email);
      
      // The actual user setup will be handled by the auth state change listener
      addToast('Account created successfully. Please check your email for verification.', 'success');
      navigate('/login');
      
    } catch (error: any) {
      console.error('AuthContext: Registration error:', error);
      // Enhanced error reporting
      let errorMessage = 'Failed to create account';
      if (error.message) {
        if (error.message.includes('already registered')) {
          errorMessage = 'Email address is already in use.';
        } else if (error.message.includes('invalid email')) {
          errorMessage = 'Invalid email address format.';
        } else if (error.message.includes('password')) {
          errorMessage = 'Password is too weak. Please use a stronger password.';
        } else {
          errorMessage = `Registration failed: ${error.message}`;
        }
      }
      setError(errorMessage);
      addToast(errorMessage, 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Reset password function
  const resetPassword = async (email: string) => {
    try {
      setLoading(true);
      setError(null);
      console.log('AuthContext: Sending password reset email...');
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`
      });
      
      if (error) {
        throw error;
      }
      
      console.log('AuthContext: Password reset email sent');
      addToast('Password reset email sent', 'success');
    } catch (error: any) {
      console.error('AuthContext: Password reset error:', error);
      // Enhanced error reporting
      let errorMessage = 'Failed to send password reset email';
      if (error.message) {
        if (error.message.includes('not found')) {
          errorMessage = 'No account found with this email address.';
        } else if (error.message.includes('invalid email')) {
          errorMessage = 'Invalid email address format.';
        } else {
          errorMessage = `Password reset failed: ${error.message}`;
        }
      }
      setError(errorMessage);
      addToast(errorMessage, 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Impersonation functions
  //
  // SECURITY (issue #19): the decision to start impersonation, the admin
  // check, and the audit write all happen SERVER-SIDE in the
  // `impersonation-session` edge function. The client no longer trusts
  // its own localStorage role to authorise impersonation, and no longer
  // mints a synthetic `impersonated_<email>` id — it uses the real staff
  // UUID returned by the server.
  //
  // NOT YET an RLS boundary: data reads still run with the admin's own
  // session JWT, so RLS evaluates as the admin during impersonation. This
  // is a verified, audited "view-as" mode; full RLS-level enforcement is
  // a separate, larger change (see PR for #19).
  const impersonateUser = async (targetUserEmail: string) => {
    try {
      // Client-side gate is a UX convenience only; the edge function is
      // the authoritative admin check.
      if (user?.role !== 'admin' && user?.role !== 'system_admin') {
        throw new Error('Only administrators can impersonate users');
      }

      console.log('AuthContext: Requesting server-verified impersonation of:', targetUserEmail);

      // Server verifies caller is an active admin, validates the target,
      // writes the audit row, and returns the target's real staff row.
      const target = await impersonationService.start(targetUserEmail);

      // Store the original admin user
      setOriginalAdmin(user);

      // Build the impersonated user from the SERVER-VALIDATED profile.
      // Keep the admin's auth object (we have not changed the JWT), but
      // use the target's real id/role/profile as returned by the server.
      const impersonatedUser: CustomUser = {
        ...user,                       // retains admin auth object (JWT unchanged)
        id: target.id,                 // real staff UUID, not a synthetic id
        email: target.email,
        name: target.name,
        role: target.role,
        department: target.department,
        curriculumArea: target.curriculumArea,
        staffCode: target.staffCode,
        staffId: target.staffId,
        fte: target.fte,
        status: target.status,
        permissions: target.permissions,
        isImpersonating: true,
        originalUser: user,
      };

      console.log('AuthContext: Impersonating user with role:', impersonatedUser.role);

      // Set the impersonated user
      setUser(impersonatedUser);
      setIsImpersonating(true);

      // Store impersonation state in localStorage (UI restore only — the
      // server already holds the authoritative audit record).
      localStorage.setItem('impersonation', JSON.stringify({
        targetEmail: target.email,
        originalEmail: user.email,
        timestamp: new Date().toISOString()
      }));

      addToast(`Now impersonating ${target.name || target.email}`, 'info');

      // Navigate to dashboard to see the impersonated view
      navigate('/dashboard');

    } catch (error) {
      console.error('AuthContext: Impersonation error:', error);
      addToast(error instanceof Error ? error.message : 'Failed to impersonate user', 'error');
      throw error;
    }
  };

  const stopImpersonation = () => {
    if (!originalAdmin) {
      console.error('AuthContext: No original admin user found');
      return;
    }

    console.log('AuthContext: Stopping impersonation, returning to admin');

    const stoppedTargetEmail = user?.email;

    // Restore the original admin user
    setUser(originalAdmin);
    setIsImpersonating(false);
    setOriginalAdmin(null);

    // Clear impersonation from localStorage
    localStorage.removeItem('impersonation');

    // Record the stop event server-side (best-effort; does not block the
    // return to the admin account).
    void impersonationService.stop(stoppedTargetEmail);

    addToast('Returned to administrator account', 'success');

    // Navigate back to user management or dashboard
    navigate('/settings/user-management');
  };

  const value = useMemo(() => ({
    user,
    loading,
    error,
    login,
    loginWithMicrosoft,
    logout,
    register,
    resetPassword,
    impersonateUser,
    stopImpersonation,
    isImpersonating
  }), [user, loading, error, isImpersonating]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};